import type { SpuAmountFilterItem, SpuAmountReportRow } from '@/services/api/spuAmountDisplay';

export const EMPTY_FILTER_VALUE = '__EMPTY__';
export const EMPTY_FILTER_LABEL = '\u65e0';

type SelectOption = {
  label: string;
  value: string;
};

const normalizeValue = (value?: string | null) => value?.trim() ?? '';

const compareOptionLabel = (left: SelectOption, right: SelectOption) => {
  if (left.value === EMPTY_FILTER_VALUE) return -1;
  if (right.value === EMPTY_FILTER_VALUE) return 1;
  return left.label.localeCompare(right.label, 'zh-CN');
};

export const displayFilterValue = (value?: string | null) => normalizeValue(value) || EMPTY_FILTER_LABEL;

export const buildBrandOptions = (records: SpuAmountFilterItem[]): SelectOption[] =>
  Array.from(new Set(records.map((item) => normalizeValue(item.brand_name)).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'zh-CN'))
    .map((value) => ({ label: value, value }));

export const buildCategoryOptions = (records: SpuAmountFilterItem[]): SelectOption[] => {
  const optionMap = new Map<string, SelectOption>();

  records.forEach((item) => {
    const rawValue = normalizeValue(item.catagory_name_lv3);
    const value = rawValue || EMPTY_FILTER_VALUE;
    optionMap.set(value, {
      value,
      label: rawValue || EMPTY_FILTER_LABEL,
    });
  });

  return Array.from(optionMap.values()).sort(compareOptionLabel);
};

export const buildSpuOptions = (records: SpuAmountFilterItem[]): SelectOption[] => {
  const optionMap = new Map<string, SelectOption>();

  records.forEach((item) => {
    const spuCode = normalizeValue(item.relation_spu_no);
    const spuName = normalizeValue(item.relation_spu_name);
    const value = spuCode || EMPTY_FILTER_VALUE;
    const label = spuCode ? (spuName ? `${spuCode} / ${spuName}` : spuCode) : EMPTY_FILTER_LABEL;

    optionMap.set(value, {
      value,
      label,
    });
  });

  return Array.from(optionMap.values()).sort(compareOptionLabel);
};

export const sanitizeSelectedValues = (selectedValues: string[], availableValues: string[]) => {
  const availableSet = new Set(availableValues);
  return selectedValues.filter((value) => availableSet.has(value));
};

export const getDisplaySpu = (row: Pick<SpuAmountReportRow, 'relation_spu' | 'spu_name' | 'relation_spu_name' | 'relation_spu_no' | 'spu_no'>) =>
  normalizeValue(row.relation_spu) ||
  normalizeValue(row.spu_name) ||
  normalizeValue(row.relation_spu_name) ||
  normalizeValue(row.relation_spu_no) ||
  normalizeValue(row.spu_no) ||
  EMPTY_FILTER_LABEL;
